import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Providers from "@/Providers.tsx";
import AuthProtectedRoute from "@/router/AuthProtectedRoute";
import { LoadingSpinner } from "@/shared/ui/loading-spinner";

// Helper function to handle both default and named exports
const lazyLoad = (importFn: () => Promise<any>, exportName?: string) => {
  return lazy(() => {
    return importFn().then(module => ({
      default: exportName ? module[exportName] : module.default || module,
    }));
  });
};

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner />
  </div>
);

// Lazy load pages with proper export handling
const Login = lazyLoad(() => import("@/pages/Login")); // Uses default export
const Dashboard = lazyLoad(() => import("@/pages/Dashboard"), "Dashboard");
const Profile = lazyLoad(() => import("@/pages/Profile"), "Profile");
const Budget = lazyLoad(() => import("@/pages/Budget"), "Budget");
const AddExpenses = lazyLoad(() => import("@/pages/AddExpenses"), "AddExpenses");
const MovementsPage = lazyLoad(() => import("@/pages/Movements"), "MovementsPage");
const AnalysisPage = lazyLoad(() => import("@/pages/Analysis"), "AnalysisPage");
const Chat = lazyLoad(() => import("@/pages/Chat"), "Chat");
const Wallet = lazyLoad(() => import("@/pages/Wallet"), "Wallet");

const router = createBrowserRouter([
  {
    path: "/",
    element: <Providers />,
    children: [
      {
        path: "/login",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: "/",
        element: <AuthProtectedRoute />,
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "/dashboard",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: "/expenses/new",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <AddExpenses />
              </Suspense>
            ),
          },
          {
            path: "/movements",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <MovementsPage />
              </Suspense>
            ),
          },
          {
            path: "/profile",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Profile />
              </Suspense>
            ),
          },
          {
            path: "/budget",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Budget />
              </Suspense>
            ),
          },
          {
            path: "/analysis",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <AnalysisPage />
              </Suspense>
            ),
          },
          {
            path: "/chat",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Chat />
              </Suspense>
            ),
          },
          {
            path: "/wallet",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <Wallet />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <div>404</div>,
  },
]);

export default router;
